import { Sparkles, BookPlus, Loader2 } from 'lucide-react';
import { Popconfirm, message } from 'antd';
import { useState, type FC } from 'react';
import { axiosInstance } from '@/dataProvider/axiosInstance';

interface AISuggestion {
  suggestedResponse: string;
  model: string;
  tokensUsed: number;
}

interface AIActionButtonsProps {
  feedbackId: string;
  onSuggestionReceived: (suggestion: AISuggestion) => void;
}

const AIActionButtons: FC<AIActionButtonsProps> = ({ feedbackId, onSuggestionReceived }) => {
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [alreadyIngested, setAlreadyIngested] = useState(false);

  const handleAiSuggest = async () => {
    setAiSuggestLoading(true);
    try {
      const { data: result } = await axiosInstance.post(`/api/v1/admin/feedbacks/${feedbackId}/ai-suggest`);
      if (result.success) {
        onSuggestionReceived(result.data);
      } else {
        message.error('Impossible de générer la suggestion IA');
      }
    } catch {
      message.error('Erreur lors de la suggestion IA');
    } finally {
      setAiSuggestLoading(false);
    }
  };

  const handleIngestToKB = async () => {
    setIngestLoading(true);
    try {
      const { data: result } = await axiosInstance.post(`/api/v1/admin/feedbacks/${feedbackId}/ingest-to-kb`);
      if (result.success) {
        message.success('Brique créée dans la Knowledge Base');
        setAlreadyIngested(true);
      } else if (result.error?.code === 'ALREADY_INGESTED') {
        message.warning('Ce feedback a déjà été ingéré');
        setAlreadyIngested(true);
      } else {
        message.error("Erreur lors de l'ingestion");
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { code?: string } } } };
      if (axiosErr.response?.data?.error?.code === 'ALREADY_INGESTED') {
        message.warning('Ce feedback a déjà été ingéré');
        setAlreadyIngested(true);
      } else {
        message.error("Erreur lors de l'ingestion");
      }
    } finally {
      setIngestLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={handleAiSuggest}
        disabled={aiSuggestLoading}
        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        {aiSuggestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        Suggérer réponse IA
      </button>
      <Popconfirm
        title="Ingérer vers la Knowledge Base ?"
        description="Ce feedback sera transformé en brique de savoir exploitable par le Growth Engine."
        onConfirm={handleIngestToKB}
        okText="Ingérer"
        cancelText="Annuler"
        okButtonProps={{ loading: ingestLoading }}
      >
        <button
          disabled={ingestLoading || alreadyIngested}
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
        >
          {ingestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookPlus className="h-4 w-4" />}
          {alreadyIngested ? 'Déjà ingéré' : 'Ingérer vers KB'}
        </button>
      </Popconfirm>
    </div>
  );
};

export default AIActionButtons;
